import {
  registerDecorator,
  type ValidationArguments,
  type ValidationOptions,
} from 'class-validator';

/**
 * Class validator that checks if exactly one of the specified fields is defined.
 * This ensures that one and only one of the fields has a value (not null or undefined).
 *
 * @example
 * class MyDto {
 *   @ExactlyOneDefined<MyDto>(['fieldB', 'fieldC'])
 *   fieldA?: string;
 *
 *   fieldB?: string;
 *   fieldC?: string;
 * }
 *
 * // Valid: Only fieldA has a value
 * // Valid: Only fieldB has a value
 * // Valid: Only fieldC has a value
 * // Invalid: fieldA and fieldB both have values
 * // Invalid: All fields are undefined/null
 */
export function ExactlyOneDefined<T>(
  properties: (keyof T)[],
  validationOptions?: ValidationOptions,
) {
  return function (object: unknown, propertyName: string) {
    registerDecorator({
      name: 'exactlyOneDefined',
      target: object.constructor,
      propertyName,
      constraints: [properties],
      options: validationOptions,
      validator: {
        validate(value: unknown, args: ValidationArguments) {
          const [relatedProperties] = args.constraints as [(keyof T)[]];
          const obj = args.object as Record<string, unknown>;

          // Check if current property has a value
          const currentHasValue = value !== null && value !== undefined;

          // Check how many related properties have values
          const relatedWithValues = relatedProperties.filter((prop) => {
            const relatedValue = obj[prop as string];
            return relatedValue !== null && relatedValue !== undefined;
          });

          // Exactly one field should have a value
          // This means: current has value XOR exactly one related has value
          if (currentHasValue) {
            // Current field has value, all related should be null/undefined
            return relatedWithValues.length === 0;
          } else {
            // Current field is null/undefined, exactly one related should have value
            return relatedWithValues.length === 1;
          }
        },

        defaultMessage(args: ValidationArguments) {
          const [relatedProperties] = args.constraints as [(keyof T)[]];
          const allFields = [args.property, ...relatedProperties.map(String)];
          return `Exactly one of ${allFields.join(', ')} must be defined`;
        },
      },
    });
  };
}
